import React, { useState } from "react";
import axios from "axios";
import { Sun, CloudRain, Cloud, Snowflake, CloudSun, Thermometer, Droplets, Wind, CalendarDays, MapPin, Loader2, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITION_ICON = {
  sunny: Sun,
  clear: CloudSun,
  cloudy: Cloud,
  rainy: CloudRain,
  cold: Snowflake,
};

const CONDITION_GRADIENT = {
  sunny: "from-[#F6B24B] to-[#E08A2B]",
  clear: "from-[#C15B6B] to-[#9E2A3B]",
  cloudy: "from-[#8FA3B0] to-[#5E7385]",
  rainy: "from-[#5E7BA8] to-[#33507E]",
  cold: "from-[#6FB0D6] to-[#3C7FB0]",
};

const POPULAR = ["İstanbul", "Ankara", "İzmir", "Bursa", "Antalya", "Samsun", "Adana", "Konya"];

const WeatherTool = () => {
  const today = new Date();
  const defaultDate = new Date(today.getTime() + 90 * 86400000).toISOString().slice(0, 10);
  const [city, setCity] = useState("");
  const [date, setDate] = useState(defaultDate);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const submit = async (e, forcedCity) => {
    if (e) e.preventDefault();
    const c = (forcedCity ?? city).trim();
    if (!c) { setError("Lütfen bir şehir girin."); return; }
    setCity(c);
    setError("");
    setLoading(true);
    setData(null);
    try {
      const res = await axios.get(`${API}/weather/forecast`, { params: { city: c, date } });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Tahmin alınamadı. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const CondIcon = data ? (CONDITION_ICON[data.condition] || CloudSun) : CloudSun;
  const grad = data ? (CONDITION_GRADIENT[data.condition] || CONDITION_GRADIENT.clear) : CONDITION_GRADIENT.clear;

  const prettyDate = (d) => {
    try {
      return new Date(d + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
    } catch { return d; }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 sm:p-8" data-testid="weather-tool">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] flex items-center justify-center">
          <CloudSun className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold text-[#2A2224]">Düğün Günü Hava Tahmini</h1>
          <p className="text-sm text-[#7C6E70]">Son 15 yılın gerçek verilerine dayalı iklim tahmini</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
        <div className="relative">
          <MapPin className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="weather-city-input"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Şehir (örn. İstanbul)"
            className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B] focus:ring-1 focus:ring-[#9E2A3B]"
          />
        </div>
        <div className="relative">
          <CalendarDays className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            data-testid="weather-date-input"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B] focus:ring-1 focus:ring-[#9E2A3B]"
          />
        </div>
        <button
          data-testid="weather-submit-btn"
          type="submit"
          disabled={loading}
          className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-lg transition-all disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Tahmin Et
        </button>
      </form>

      <div className="mt-3 flex flex-wrap gap-2">
        {POPULAR.map((c) => (
          <button
            key={c}
            onClick={() => submit(null, c)}
            data-testid={`weather-popular-${c}`}
            className="px-3 py-1.5 rounded-full bg-[#FBF3E4] text-[#8a6d1f] text-xs font-medium hover:bg-[#F3E6C6] transition-colors"
          >
            {c}
          </button>
        ))}
      </div>

      {error && (
        <div data-testid="weather-error" className="mt-5 px-4 py-3 rounded-xl bg-[#FBEAEA] text-[#9E2A3B] text-sm">
          {error}
        </div>
      )}

      {data && (
        <div className="mt-6" data-testid="weather-result">
          <div className={`rounded-2xl bg-gradient-to-br ${grad} text-white p-6 sm:p-8`}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="w-24 h-24 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
                <CondIcon className="w-14 h-14 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white/80 text-sm">{data.city}{data.region ? `, ${data.region}` : ""} · {prettyDate(data.date)}</p>
                <div className="flex items-end gap-2 mt-1">
                  <span className="text-5xl font-bold leading-none">{Math.round(data.avgDay)}°</span>
                  <span className="text-white/85 mb-1 text-sm">ortalama</span>
                </div>
                <p className="mt-2 text-lg font-medium">{data.conditionLabel}</p>
                <p className="text-white/80 text-sm mt-1">
                  Gündüz ~{Math.round(data.avgMax)}° / gece ~{Math.round(data.avgMin)}°
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            <Stat icon={Droplets} label="Yağış ihtimali" value={`%${data.rainProbability}`} />
            <Stat icon={Thermometer} label="En yüksek (rekor)" value={`${Math.round(data.recordMax)}°`} />
            <Stat icon={Snowflake} label="En düşük (rekor)" value={`${Math.round(data.recordMin)}°`} />
            <Stat icon={Wind} label="Ort. rüzgar" value={`${Math.round(data.avgWind)} km/s`} />
          </div>

          <p className="mt-4 text-xs text-[#9c8f90] leading-relaxed">
            Bu tahmin, {data.city} için son {data.yearsAnalyzed} yılın aynı tarih aralığındaki
            {" "}{data.samples} günlük gerçek hava kaydı analiz edilerek hesaplanmıştır. İleri tarihli düğünler için
            {" "}iklim beklentisi sunar; kesin bir meteorolojik tahmin değildir. Düğün gününüz yaklaştıkça (son 15 gün)
            {" "}güncel tahminleri kontrol etmenizi öneririz.
          </p>
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-[#F0DDE0] bg-[#FBF5F0] p-4">
    <Icon className="w-5 h-5 text-[#C15B6B] mb-2" />
    <p className="text-xs text-[#7C6E70]">{label}</p>
    <p className="text-lg font-bold text-[#2A2224]">{value}</p>
  </div>
);

export default WeatherTool;
